import { PROXY_API_V1_URL } from '../core'
import { consumeSSE } from '../../agents/sse-client'

const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

export type NotificationCreatedStreamStatus = 'connected' | 'disconnected'
export type NotificationCreatedStreamEventType = 'ready' | 'signal' | 'heartbeat'

export interface SubscribeNotificationCreatedSignalsOptions {
  onSignal: () => void
  onStatus?: (status: NotificationCreatedStreamStatus) => void
  signal: AbortSignal
}

export function nextNotificationSignalBackoffMs(attempt: number): number {
  return Math.min(INITIAL_BACKOFF_MS * 2 ** Math.max(0, attempt), MAX_BACKOFF_MS)
}

export function notificationCreatedStreamEventType(
  event: unknown
): NotificationCreatedStreamEventType | undefined {
  if (!event || typeof event !== 'object') return undefined
  const type = (event as { type?: unknown }).type
  return type === 'ready' || type === 'signal' || type === 'heartbeat' ? type : undefined
}

export function isNotificationCreatedStreamUnavailable(error: unknown): boolean {
  return error instanceof Error && error.message.includes('HTTP 503')
}

/**
 * Reconnecting SSE wake-ups for new notifications. Kept open while the tab is
 * backgrounded (unlike GraphChangePoller) so desktop alerts can fire without
 * waiting on throttled polling. The 30s list poll remains the recovery path.
 */
export async function subscribeNotificationCreatedSignals({
  onSignal,
  onStatus,
  signal
}: SubscribeNotificationCreatedSignalsOptions): Promise<void> {
  let attempt = 0
  while (!signal.aborted) {
    let connected = false
    let failure: unknown
    try {
      await consumeSSE<unknown>({
        url: `${PROXY_API_V1_URL}/notifications/change-stream`,
        body: {},
        signal,
        onEvent: (event) => {
          if (signal.aborted) return
          const type = notificationCreatedStreamEventType(event)
          if (type === 'ready') {
            connected = true
            attempt = 0
            onStatus?.('connected')
          } else if (type === 'signal') {
            onSignal()
          }
        }
      })
    } catch (error) {
      failure = error
    }
    if (signal.aborted) return

    onStatus?.('disconnected')
    const delayMs = isNotificationCreatedStreamUnavailable(failure)
      ? MAX_BACKOFF_MS
      : nextNotificationSignalBackoffMs(attempt)
    if (!connected) attempt += 1
    await abortableDelay(delayMs, signal)
  }
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true }
    )
  })
}
